import React from 'react';

// Inline styles - NO Amplify CSS, NO theme switching.
// Shared across the AuthWrapper gate and the auth form components.
export const styles: { [key: string]: React.CSSProperties } = {
  pageContainer: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    position: 'relative',
    overflow: 'hidden',
    transition: 'none',
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.03) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    transition: 'none',
  },
  loadingContainer: {
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'none',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #334155',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '48px',
    width: '520px',
    maxWidth: '90vw',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
    transition: 'none',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '2rem',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
    position: 'relative' as const,
  },
  logoGlow: {
    position: 'absolute' as const,
    width: '80px',
    height: '80px',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
    filter: 'blur(20px)',
    zIndex: 0,
  },
  logo: {
    position: 'relative' as const,
    zIndex: 1,
    filter: 'drop-shadow(0 8px 16px rgba(59, 130, 246, 0.4))',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '0.5rem',
    letterSpacing: '-0.025em',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#94a3b8',
    fontWeight: 500,
    letterSpacing: '0.025em',
    marginBottom: '0.5rem',
    margin: '0 0 0.5rem 0',
  },
  tagline: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontWeight: 400,
    fontStyle: 'italic' as const,
    margin: 0,
  },
  contextualMessage: {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    color: '#93c5fd',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #334155',
    marginBottom: '1.5rem',
    gap: 0,
  },
  tab: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '0.75rem 1.5rem',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    color: '#ffffff',
    borderBottomColor: '#3b82f6',
  },
  form: {
    marginTop: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    height: '48px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.9375rem',
    color: '#ffffff',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.2s',
  },
  button: {
    width: '100%',
    height: '48px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.2s',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  linkButton: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '1rem',
    padding: '0.5rem',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fca5a5',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  success: {
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#86efac',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  confirmText: {
    color: '#cbd5e1',
    fontSize: '0.9375rem',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '1px solid #334155',
    fontSize: '0.75rem',
    color: '#475569',
  },
  forgotPasswordLink: {
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
    padding: 0,
    textAlign: 'left' as const,
    fontWeight: 500,
    transition: 'color 0.2s',
  },
  tosContainer: {
    marginBottom: '1.25rem',
  },
  tosLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.625rem',
    cursor: 'pointer',
  },
  tosCheckbox: {
    marginTop: '2px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    accentColor: '#3b82f6',
    cursor: 'pointer',
  },
  tosText: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    lineHeight: 1.5,
  },
  tosLink: {
    color: '#60a5fa',
    textDecoration: 'underline',
  },
  tosError: {
    marginTop: '0.5rem',
    fontSize: '0.8125rem',
    color: '#fca5a5',
    margin: '0.5rem 0 0 0',
  },
};
